import { useState } from "react";
import { toast } from "react-toastify";
import {
  useGetResignationByEmployeeQuery,
  useReviewResignationMutation,
  useReviewResignationDocumentsMutation,
  useDeactivateEmployeeMutation,
  useWithdrawResignationMutation,
} from "../../api/resignationApi";
import {
  RESIGNATION_STATUS,
  addDays,
  apiErrorMessage,
  canRemoveEmployee,
  computeLastWorkingDay,
  daysBetween,
  effectiveStatus,
  formatDate,
  formatNoticePeriod,
  getNoticeDays,
  normalizeResignation,
  toDateInputValue,
} from "./constants";
import StatusBadge from "./StatusBadge";
import FlowProgress from "./FlowProgress";
import ResignationActivity from "./ResignationActivity";
import DocumentList from "./DocumentList";
import ReasonDialog from "./ReasonDialog";
import UploadDocuments from "./UploadDocuments";

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs uppercase tracking-wide text-gray-400">{label}</p>
    <p className="mt-1 text-sm text-gray-700">{value || "-"}</p>
  </div>
);

const AdminResignationDetail = ({ employeeId, onBack }) => {
  const { data, isLoading, isError, error, refetch } =
    useGetResignationByEmployeeQuery(employeeId, { skip: !employeeId });
  const [reviewResignation, { isLoading: isReviewing }] = useReviewResignationMutation();
  const [reviewDocuments, { isLoading: isReviewingDocs }] =
    useReviewResignationDocumentsMutation();
  const [deactivateEmployee, { isLoading: isRemoving }] = useDeactivateEmployeeMutation();
  const [withdrawResignation, { isLoading: isWithdrawing }] = useWithdrawResignationMutation();

  const [dialog, setDialog] = useState(null);
  const [lastWorkingDay, setLastWorkingDay] = useState("");

  const record = normalizeResignation(data);
  const status = effectiveStatus(record);
  const busy = isReviewing || isReviewingDocs || isRemoving || isWithdrawing;
  // null when no notice period is recorded, 0 when it is genuinely zero.
  const noticeDays = getNoticeDays(record);
  const canRemove = canRemoveEmployee(record);
  const isApprovingRequest = dialog?.scope === "REQUEST" && dialog?.decision === "APPROVE";
  const chosenDelta =
    isApprovingRequest && lastWorkingDay ? daysBetween(new Date(), lastWorkingDay) : null;
  const openRequestReview = (decision) => {
    if (decision === "APPROVE") {
      setLastWorkingDay(toDateInputValue(computeLastWorkingDay(record?.noticePeriodDays)));
    }
    setDialog({ scope: "REQUEST", decision });
  };

  const handleConfirm = async (reason) => {
    if (!dialog) return;
    if (isApprovingRequest && !lastWorkingDay) {
      toast.error("Please set the employee's last working day.");
      return;
    }
    if (dialog.scope === "WITHDRAW") {
      try {
        await withdrawResignation({ employeeId: record.employeeId || employeeId }).unwrap();
        setDialog(null);
        toast.success(`Resignation withdrawn for ${record.employeeName || "the employee"}.`);
      } catch (err) {
        toast.error(apiErrorMessage(err, "Could not withdraw this resignation."));
      }
      return;
    }
    if (dialog.scope === "REMOVE") {
      try {
        await deactivateEmployee({ employeeId }).unwrap();
        setDialog(null);
        toast.success(`${record.employeeName || "Employee"} removed. Their login is revoked.`);
        onBack();
      } catch (err) {
        toast.error(apiErrorMessage(err, "Could not remove this employee."));
      }
      return;
    }

    const payload = {
      employeeId,
      decision: dialog.decision,
      ...(dialog.decision === "REJECT" ? { rejectionReason: reason } : {}),
      ...(isApprovingRequest ? { lastWorkingDay } : {}),
    };
    const action = dialog.scope === "DOCUMENTS" ? reviewDocuments : reviewResignation;
    try {
      await action(payload).unwrap();
      setDialog(null);
      toast.success(
        dialog.scope === "DOCUMENTS"
          ? dialog.decision === "APPROVE"
            ? "Exit documents approved. The resignation is now closed."
            : "Documents sent back to the employee for re-upload."
          : dialog.decision === "APPROVE"
          ? `Resignation approved. Last working day set to ${formatDate(lastWorkingDay)}.`
          : "Resignation rejected."
      );
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not record your decision."));
    }
  };

  if (isLoading) {
    return <p className="p-6 text-sm text-gray-500">Loading resignation details...</p>;
  }

  if (isError || !record) {
    return (
      <div className="p-6">
        <button
          type="button"
          onClick={onBack}
          className="text-sm font-medium text-blue-primary hover:underline"
        >
          &larr; Back to queue
        </button>
        <p className="mt-4 text-sm text-red-600">
          {apiErrorMessage(error, "Could not load this resignation record.")}
        </p>
        <button
          type="button"
          onClick={refetch}
          className="mt-3 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
        >
          Retry
        </button>
      </div>
    );
  }

  const dialogConfig = dialog
    ? dialog.scope === "WITHDRAW"
      ? {
          title: "Withdraw this resignation?",
          description: `This withdraws ${
            record.employeeName || "the employee"
          }'s pending resignation on their behalf. They can submit a new one later.`,
          confirmLabel: "Withdraw",
          confirmClassName: "bg-amber-500 hover:bg-amber-600",
          requireReason: false,
        }
      : dialog.scope === "REMOVE"
      ? {
          title: "Remove this employee?",
          description:
            "Once this action is performed the employee will no longer be able to log in.",
          confirmLabel: "Remove Employee",
          confirmClassName: "bg-red-600 hover:bg-red-700",
          requireReason: false,
        }
      : dialog.scope === "DOCUMENTS"
      ? dialog.decision === "APPROVE"
        ? {
            title: "Approve exit documents?",
            description:
              "This closes the resignation. The employee will see the exit clearance as complete.",
            confirmLabel: "Approve Documents",
            confirmClassName: "bg-emerald-500 hover:bg-emerald-600",
            requireReason: false,
          }
        : {
            title: "Reject exit documents?",
            description:
              "The employee is sent back to the upload screen with your reason shown.",
            confirmLabel: "Reject Documents",
            confirmClassName: "bg-red-500 hover:bg-red-600",
            requireReason: true,
            reasonLabel: "Reason for rejection",
            placeholder: "e.g. Please re-upload a signed copy of the asset return form.",
          }
      : dialog.decision === "APPROVE"
      ? {
          title: "Approve resignation?",
          description:
            "Confirm the last working day, then the employee can upload their exit-clearance documents.",
          confirmLabel: "Approve",
          confirmClassName: "bg-emerald-500 hover:bg-emerald-600",
          requireReason: false,
        }
      : {
          title: "Reject resignation?",
          description:
            "The request is cancelled and the employee can submit a new resignation later.",
          confirmLabel: "Reject",
          confirmClassName: "bg-red-500 hover:bg-red-600",
          requireReason: true,
          reasonLabel: "Reason for rejection",
          placeholder: "Let the employee know why the request was rejected.",
        }
    : {};

  const lastWorkingDayField = isApprovingRequest ? (
    <div className="mt-4">
      <label htmlFor="lastWorkingDay" className="block text-sm font-medium text-gray-600">
        Last Working Day <span className="text-red-500">*</span>
      </label>
      <input
        id="lastWorkingDay"
        type="date"
        value={lastWorkingDay}
        onChange={(event) => setLastWorkingDay(event.target.value)}
        className="mt-1 w-full rounded-lg border border-gray-300 p-2 text-sm outline-none focus:border-blue-primary"
      />

      {noticeDays === null ? (
        <p className="mt-2 text-xs text-amber-600">
          No notice period is recorded for this employee, so this defaults to today. Please
          set the correct date.
        </p>
      ) : noticeDays === 0 ? (
        <p className="mt-2 text-xs text-gray-500">
          This employee's notice period is 0 days, so the last working day defaults to
          today.
        </p>
      ) : (
        <p className="mt-2 text-xs text-gray-500">
          Pre-filled as today + {noticeDays} {noticeDays === 1 ? "day" : "days"}, the notice
          period on this employee's record.
        </p>
      )}

      {chosenDelta !== null && chosenDelta < 0 && (
        <p className="mt-1 text-xs text-red-600">
          This date has already passed.
        </p>
      )}

      {chosenDelta !== null && chosenDelta >= 0 && noticeDays !== null && chosenDelta !== noticeDays && (
        <p className="mt-1 text-xs text-amber-600">
          {Math.abs(noticeDays - chosenDelta)}{" "}
          {Math.abs(noticeDays - chosenDelta) === 1 ? "day" : "days"}{" "}
          {chosenDelta < noticeDays ? "shorter" : "longer"} than the {noticeDays}-day notice
          period.
        </p>
      )}

      <p className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-2 text-xs text-gray-500">
        This is saved as the employee's official last working day and drives their final
        payroll and exit clearance. Change it only if the notice period is being waived or
        extended.
      </p>
    </div>
  ) : null;

  const removalDisclaimer =
    dialog?.scope === "REMOVE" ? (
      <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3">
        <p className="text-sm font-medium text-red-700">This cannot be undone here</p>
        <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-red-600">
          <li>
            {record.employeeName || "The employee"} will no longer be able to log in.
          </li>
          <li>Their employee record is deactivated across the app.</li>
          <li>
            Last working day was {formatDate(record.lastWorkingDay)} - make sure final
            payroll is settled first.
          </li>
        </ul>
      </div>
    ) : null;

  return (
    <div className="space-y-5">
      <button
        type="button"
        onClick={onBack}
        className="text-sm font-medium text-blue-primary hover:underline"
      >
        &larr; Back to queue
      </button>

      <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-800">
              {record.employeeName || "Employee"}
            </h2>
            <p className="text-sm text-gray-400">{record.employeeNum}</p>
          </div>
          <StatusBadge status={status} />
        </div>

        <div className="mt-5">
          <FlowProgress status={status} />
        </div>

        <ResignationActivity record={record} viewer="admin" />

        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Reason" value={record.reason} />
          <Field
            label="Notice Period"
            value={formatNoticePeriod(record)}
          />
          <Field label="Submitted On" value={formatDate(record.submittedDate, true)} />
          <Field label="Approved On" value={formatDate(record.approvedDate, true)} />
          <Field label="Last Working Day" value={formatDate(record.lastWorkingDay)} />
          <Field
            label="Documents Reviewed On"
            value={formatDate(record.reviewedDocumentsDate, true)}
          />
        </div>

        {record.rejectionReason && status === RESIGNATION_STATUS.IDLE && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-sm font-medium text-red-700">Request rejected</p>
            <p className="mt-1 text-sm text-red-600">{record.rejectionReason}</p>
          </div>
        )}

        {status === RESIGNATION_STATUS.PENDING && (
          <div className="mt-5 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              disabled={busy}
              onClick={() => setDialog({ scope: "WITHDRAW" })}
              className="rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              Withdraw on Behalf
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => openRequestReview("REJECT")}
              className="rounded-lg border border-red-300 px-5 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
            >
              Reject
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => openRequestReview("APPROVE")}
              className="rounded-lg bg-blue-primary px-5 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
            >
              Approve
            </button>
          </div>
        )}
      </section>

      {status !== RESIGNATION_STATUS.IDLE && status !== RESIGNATION_STATUS.PENDING && (
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800">Exit Clearance Documents</h3>

          {record.documentsRejectionReason && (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm font-medium text-amber-700">
                Last review sent the documents back
              </p>
              <p className="mt-1 text-sm text-amber-600">{record.documentsRejectionReason}</p>
            </div>
          )}

          <div className="mt-4">
            <DocumentList
              documents={record.documents}
              emptyText={
                status === RESIGNATION_STATUS.APPROVED
                  ? "The employee has not uploaded any documents yet."
                  : "No documents on this record."
              }
            />
          </div>

          {status === RESIGNATION_STATUS.UPLOADED && (
            <div className="mt-5 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                disabled={busy}
                onClick={() => setDialog({ scope: "DOCUMENTS", decision: "REJECT" })}
                className="rounded-lg border border-red-300 px-5 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                Reject Documents
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => setDialog({ scope: "DOCUMENTS", decision: "APPROVE" })}
                className="rounded-lg bg-blue-primary px-5 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
              >
                Approve Documents
              </button>
            </div>
          )}
        </section>
      )}

      {status === RESIGNATION_STATUS.APPROVED && (
        <UploadDocuments
          employeeId={record.employeeId || employeeId}
          rejectionReason={null}
          onBehalf
        />
      )}

      {status === RESIGNATION_STATUS.COMPLETED && (
        <section className="rounded-xl border border-red-200 bg-white p-5 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-800">Remove Employee</h3>
          <p className="mt-1 text-sm text-gray-500">
            Exit clearance is complete. Removing the employee deactivates their record and
            revokes their login.
          </p>

          {!record.lastWorkingDay ? (
            <p className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
              No last working day is recorded on this resignation, so removal is not
              available. It is set when the resignation is approved.
            </p>
          ) : canRemove ? (
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-gray-500">
                Last working day was {formatDate(record.lastWorkingDay)}.
              </p>
              <button
                type="button"
                disabled={busy}
                onClick={() => setDialog({ scope: "REMOVE" })}
                className="rounded-lg bg-red-600 px-5 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                Remove Employee
              </button>
            </div>
          ) : (
            <p className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500">
              Available from {formatDate(addDays(record.lastWorkingDay, 1))}. The employee is
              still on the payroll until {formatDate(record.lastWorkingDay)}.
            </p>
          )}
        </section>
      )}

      <ReasonDialog
        open={Boolean(dialog)}
        isLoading={busy}
        confirmDisabled={isApprovingRequest && !lastWorkingDay}
        onConfirm={handleConfirm}
        onClose={() => setDialog(null)}
        {...dialogConfig}
      >
        {lastWorkingDayField}
        {removalDisclaimer}
      </ReasonDialog>
    </div>
  );
};

export default AdminResignationDetail;
